import { translate } from "@/utils/i18n";

export enum UserRole {
  Admin = "Admin",
  SuperAdmin = "SuperAdmin",
  Doctor = "Doctor",
  Nurse = "Nurse",
  Guardian = "Guardian",
  Patient = "Patient",
  Cro = "Cro",
  Unknown = "Unknown",
}

export type Tokens = {
  accessToken: string;
  refreshToken: string;
};

export type UserSession = {
  email: string;
  name: string;
  role: UserRole;
  isExpiredSn: boolean;
};

export type LoginResponseDto = {
  accessToken?: string;
  refreshToken?: string;
  isExpiredSn?: boolean;
  email?: string;
  role?: string;
  name?: string;
};

export type ReissueTokenResponseDto = {
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  role?: string;
  name?: string;
  isExpiredSn?: boolean;
};

export function normalizeRole(value: string | null | undefined): UserRole {
  switch ((value ?? "").toLowerCase()) {
    case "admin":
      return UserRole.Admin;
    case "superadmin":
    case "super_admin":
      return UserRole.SuperAdmin;
    case "doctor":
      return UserRole.Doctor;
    case "nurse":
      return UserRole.Nurse;
    case "guardian":
      return UserRole.Guardian;
    case "patient":
      return UserRole.Patient;
    case "cro":
      return UserRole.Cro;
    default:
      return UserRole.Unknown;
  }
}

export function getRoleFromString(role: string): UserRole {
  const normalizedRole = normalizeRole(role);
  if (normalizedRole === UserRole.Unknown) {
    throw new Error(`[getRoleFromString] invalid role: ${role}`);
  }
  return normalizedRole;
}

export function roleLabel(role: UserRole): string {
  switch (role) {
    case UserRole.Admin:
    case UserRole.SuperAdmin:
      return translate("role.admin");
    case UserRole.Doctor:
      return translate("role.medicalExpert");
    case UserRole.Nurse:
      return translate("role.nurse");
    case UserRole.Guardian:
      return translate("role.guardian");
    case UserRole.Patient:
      return translate("role.patient");
    case UserRole.Cro:
      return translate("role.cro");
    default:
      return translate("role.unknown");
  }
}

export function webAccountRoleLabel(role: UserRole): string {
  if (isAdmin(role)) return translate("role.admin");
  if (role === UserRole.Guardian) return translate("role.guardian");
  if (role === UserRole.Patient) return translate("role.patient");
  if (role === UserRole.Unknown) return translate("role.unknown");
  return translate("role.medicalExpert");
}

export function isAdmin(role: UserRole): boolean {
  return role === UserRole.Admin || role === UserRole.SuperAdmin;
}

export function isPractitioner(role: UserRole): boolean {
  return role === UserRole.Doctor || role === UserRole.Nurse || role === UserRole.Cro;
}

export function isMedicalExpertRole(role: UserRole): boolean {
  return role === UserRole.Doctor || role === UserRole.Nurse || role === UserRole.Cro;
}

export function isCareTeamRole(role: UserRole): boolean {
  return role === UserRole.Doctor || role === UserRole.Nurse;
}

export function canUseNotification(role: UserRole): boolean {
  return role === UserRole.Doctor;
}

export function canCreatePatient(role: UserRole): boolean {
  return role === UserRole.Doctor;
}

export function canEditPatientInfo(role: UserRole): boolean {
  return role === UserRole.Doctor;
}

export function canManagePrescription(role: UserRole): boolean {
  return role === UserRole.Doctor;
}

export function canSendPatientMessage(role: UserRole): boolean {
  return role === UserRole.Doctor;
}

export function canViewPatientIdentifiableInfo(role: UserRole): boolean {
  return role === UserRole.Doctor;
}

export function canViewPatientMemo(role: UserRole): boolean {
  return role === UserRole.Doctor;
}

export function getHomeHref(role: UserRole): string {
  if (isAdmin(role)) return "/admin/home";
  if (isMedicalExpertRole(role)) return "/home";
  return "/";
}

export function getPasswordChangeHref(roleOrPath: UserRole | string | null | undefined): string {
  if (!roleOrPath) return "/";

  const role =
    typeof roleOrPath === "string" && roleOrPath.startsWith("/")
      ? roleFromPath(roleOrPath)
      : normalizeRole(String(roleOrPath));

  if (isAdmin(role)) return "/admin/home/password-change";
  if (isMedicalExpertRole(role)) return "/home/password-change";
  return "/";
}

export function roleFromPath(pathname: string): UserRole {
  if (pathname.startsWith("/admin")) return UserRole.Admin;
  if (pathname.startsWith("/home")) return UserRole.Doctor;
  return UserRole.Unknown;
}
